import React, { useRef, useState, useEffect } from "react";
import { Plus, CaretDown, X, MagnifyingGlass, Polygon, MapTrifold, UploadSimple, Warning } from "@phosphor-icons/react";

/* ---- New project: entry button, method menu and the shared panel chrome ----
   The sandbox map offers one "New project" button. It opens a menu of the ways
   to start a project; picking one opens the shared panel on that method, and
   the tabs in the panel header switch between methods without closing it. */

export const METHODS = [
	{ key: "screen", tab: "Screened", title: "Screened areas", desc: "Pick a candidate area we've already screened", Icon: MagnifyingGlass, accent: true },
	{ key: "draw", tab: "Draw", title: "Draw on map", desc: "Outline the project area yourself", Icon: Polygon },
	{ key: "cad", tab: "Cadastral", title: "Cadastral parcels", desc: "Search or paste cadastral numbers", Icon: MapTrifold },
	{ key: "upload", tab: "Upload", title: "Upload file", desc: "Shape, GeoJSON, GPKG or CSV", Icon: UploadSimple }
];

/* Button + dropdown menu. */
export function NewProjectMenu({ onPick, autoFocus = false }) {
	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);
	const buttonRef = useRef(null);
	const itemRefs = useRef([]);

	useEffect(() => {
		if (autoFocus && buttonRef.current) buttonRef.current.focus();
	}, [autoFocus]);

	// Close on click outside
	useEffect(() => {
		if (!open) return;
		const onDown = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		return () => document.removeEventListener("mousedown", onDown);
	}, [open]);

	// Focus the first item when the menu opens
	useEffect(() => {
		if (open && itemRefs.current[0]) itemRefs.current[0].focus();
	}, [open]);

	const close = () => {
		setOpen(false);
		if (buttonRef.current) buttonRef.current.focus();
	};

	const onMenuKeyDown = (e) => {
		const items = itemRefs.current.filter(Boolean);
		const idx = items.indexOf(document.activeElement);
		if (e.key === "ArrowDown") {
			e.preventDefault();
			items[(idx + 1) % items.length].focus();
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			items[(idx - 1 + items.length) % items.length].focus();
		} else if (e.key === "Home") {
			e.preventDefault();
			items[0].focus();
		} else if (e.key === "End") {
			e.preventDefault();
			items[items.length - 1].focus();
		} else if (e.key === "Escape" || e.key === "Tab") {
			e.preventDefault();
			close();
		}
	};

	return (
		<div className="np-entry" ref={rootRef}>
			<button
				ref={buttonRef}
				className="np-button"
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen(!open)}
				onKeyDown={(e) => { if (e.key === "ArrowDown" && !open) { e.preventDefault(); setOpen(true); } }}>
				<Plus size={18} weight="bold" aria-hidden="true" />
				New project
				<CaretDown size={14} weight="bold" aria-hidden="true" className="np-caret" />
			</button>

			{open && (
				<div className="np-menu" role="menu" aria-label="Start a new project from" onKeyDown={onMenuKeyDown}>
					<div className="np-menu-title">Start from</div>
					{METHODS.map((m, i) => (
						<button
							key={m.key}
							ref={(el) => { itemRefs.current[i] = el; }}
							className="np-menu-item"
							role="menuitem"
							onClick={() => { setOpen(false); onPick(m.key); }}>
							<span className={"np-tile " + (m.accent ? "accent" : "")} aria-hidden="true">
								<m.Icon size={20} />
							</span>
							<span className="np-menu-text">
								<span className="ttl">{m.title}</span>
								<span className="desc">{m.desc}</span>
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

/* Title, close button and method tabs, rendered at the top of whichever
   method panel is active. */
export function NewProjectHeader({ method, onChange, onClose }) {
	const tabRefs = useRef({});

	// Move focus into the panel when it opens / the method changes
	useEffect(() => {
		const el = tabRefs.current[method];
		if (el && !el.contains(document.activeElement) && !document.activeElement?.closest?.(".screening-panel"))
			el.focus();
	}, [method]);

	const onTabKeyDown = (e) => {
		const idx = METHODS.findIndex((m) => m.key === method);
		let next = null;
		if (e.key === "ArrowRight") next = METHODS[(idx + 1) % METHODS.length];
		else if (e.key === "ArrowLeft") next = METHODS[(idx - 1 + METHODS.length) % METHODS.length];
		else if (e.key === "Home") next = METHODS[0];
		else if (e.key === "End") next = METHODS[METHODS.length - 1];
		if (!next) return;
		e.preventDefault();
		onChange(next.key);
		tabRefs.current[next.key] && tabRefs.current[next.key].focus();
	};

	return (
		<div className="np-header">
			<div className="np-title-row">
				<h1>New project</h1>
				<button className="np-close" aria-label="Close panel" onClick={onClose}>
					<X size={18} aria-hidden="true" />
				</button>
			</div>
			<div className="np-tabs" role="tablist" aria-label="How to create the project" onKeyDown={onTabKeyDown}>
				{METHODS.map((m) => {
					const sel = m.key === method;
					return (
						<button
							key={m.key}
							ref={(el) => { tabRefs.current[m.key] = el; }}
							className={"np-tab " + (sel ? "on" : "")}
							role="tab"
							aria-selected={sel}
							tabIndex={sel ? 0 : -1}
							onClick={() => onChange(m.key)}>
							<m.Icon size={18} aria-hidden="true" />
							{m.tab}
						</button>
					);
				})}
			</div>
		</div>
	);
}

/* Panel shell for methods that have no overlay component of their own
   (Draw and Upload). Same layout as the screening/cadastral panels; footer
   is pinned to the bottom of the panel. */
export function MethodPanel({ header, footer, children }) {
	return (
		<div className="screening-main screening-overlay">
			<aside className="screening-panel np-embedded">
				{header}
				<div className="np-body">{children}</div>
				{footer}
			</aside>
		</div>
	);
}

/* Summary + Clear + Create project at the bottom of the panel, the same bar
   the screening and cadastral tabs show for their selection. */
export function CreateFooter({ title, meta, onClear, onCreate, disabled = false }) {
	return (
		<div className="selection-bar in-panel">
			<div className="sel-summary">
				<span className="sel-count">{title}</span>
				{meta && <span className="sel-meta">{meta}</span>}
			</div>
			<button className="btn btn-ghost btn-sm" onClick={onClear}>Clear</button>
			<button className="btn btn-success" onClick={onCreate} disabled={disabled}>
				<Plus size={16} /> Create project
			</button>
		</div>
	);
}

/* Warning for an outline that crosses itself or has overlapping parts, with
   the same fix as "Flatten polygon" on the project boundary. */
export function InvalidShape({ shape, onFix }) {
	const crossings = shape.kinks.length;
	return (
		<div className="np-invalid" role="alert">
			<div className="np-invalid-title"><Warning size={16} weight="bold" aria-hidden="true" /> Invalid shape</div>
			<div className="np-invalid-text">
				{crossings > 0 && <>The outline crosses itself in {crossings} place{crossings > 1 ? "s" : ""} (marked in red on the map). </>}
				{shape.overlaps > 0 && <>{shape.overlaps} part{shape.overlaps > 1 ? "s" : ""} of the shape overlap. </>}
				A project boundary must be a valid polygon.
			</div>
			<button className="btn btn-outline btn-sm" onClick={onFix}>Fix shape</button>
		</div>
	);
}
